/**
 * Mark cold/lost: lists the person's open leads, with the leads behind the
 * follow-up's reasons already ticked. Cold sets each ticked lead's
 * temperature; lost moves it to Closed Lost. There is no undo here: a lost
 * lead can be moved back on the Leads board, and a cold one changed in Edit
 * lead.
 *
 * If some leads fail, the sheet stays open with only those still ticked and
 * says why.
 */

import { Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import {
	type LeadMark,
	useMarkLeads,
} from "@/features/realtor/hooks/use-realtor-crm";
import type { FollowUpEntry } from "@/features/realtor/services/realtor-crm-api.service";
import { StudioButton } from "@/features/studio/theme";

/** The leads the follow-up's reasons point at, in the order listed. */
export function reasonLeadIds(entry: FollowUpEntry): string[] {
	const fromReasons = new Set(
		entry.reasons.map((reason) => reason.leadId).filter(Boolean),
	);
	return entry.leads
		.filter((lead) => fromReasons.has(lead.id))
		.map((lead) => lead.id);
}

export function MarkLeadsSheet({
	entry,
	open,
	onOpenChange,
}: {
	entry: FollowUpEntry;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { t } = useTranslation();
	const ids = useId();
	const markLeads = useMarkLeads();
	const [selected, setSelected] = useState<Set<string>>(
		() => new Set(reasonLeadIds(entry)),
	);
	const [error, setError] = useState<string | null>(null);
	const busy = markLeads.isPending;

	const toggle = (leadId: string) =>
		setSelected((current) => {
			const next = new Set(current);
			if (next.has(leadId)) next.delete(leadId);
			else next.add(leadId);
			return next;
		});

	const close = (next: boolean) => {
		if (busy) return;
		if (!next) {
			setSelected(new Set(reasonLeadIds(entry)));
			setError(null);
		}
		onOpenChange(next);
	};

	const mark = async (how: LeadMark) => {
		// Keep the listed order rather than the order they were ticked.
		const leadIds = entry.leads
			.map((lead) => lead.id)
			.filter((id) => selected.has(id));
		if (leadIds.length === 0 || busy) return;
		setError(null);
		let failed: string[];
		let message: string | null;
		try {
			const result = await markLeads.mutateAsync({
				contactId: entry.contact.id,
				leadIds,
				mark: how,
			});
			failed = result.failed;
			message = result.error;
		} catch (failure: unknown) {
			failed = leadIds;
			message = failure instanceof Error ? failure.message : null;
		}
		if (failed.length === 0) {
			close(false);
			return;
		}
		setSelected(new Set(failed));
		setError(message || t("realtor.followUps.mark.failed"));
	};

	return (
		<Sheet open={open} onOpenChange={close}>
			<SheetContent
				side="bottom"
				className="mx-auto max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-xl border-dashboard-border bg-dashboard-bg"
			>
				<SheetHeader>
					<SheetTitle className="font-display text-xl italic font-medium text-dashboard-text">
						{t("realtor.followUps.mark.title", { name: entry.contact.name })}
					</SheetTitle>
					<SheetDescription className="text-sm text-dashboard-muted">
						{t("realtor.followUps.mark.description")}
					</SheetDescription>
				</SheetHeader>
				<fieldset className="mt-4 space-y-2 px-4">
					<legend className="sr-only">
						{t("realtor.followUps.mark.leadsLegend")}
					</legend>
					{entry.leads.map((lead) => (
						<label
							key={lead.id}
							htmlFor={`${ids}-${lead.id}`}
							className="flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border border-dashboard-border bg-dashboard-paper px-3 py-2"
						>
							<input
								id={`${ids}-${lead.id}`}
								type="checkbox"
								className="h-5 w-5 accent-[var(--color-dashboard-accent)]"
								checked={selected.has(lead.id)}
								disabled={busy}
								onChange={() => toggle(lead.id)}
							/>
							<span className="min-w-0 flex-1">
								<span className="block truncate text-sm font-medium text-dashboard-text">
									{lead.name}
								</span>
								<span className="block text-xs text-dashboard-muted">
									{t(`realtor.leads.types.${lead.type}`)} ·{" "}
									{t(`realtor.leads.stages.${lead.stage}`)} ·{" "}
									{t(`realtor.leads.temperatures.${lead.temperature}`)}
								</span>
							</span>
						</label>
					))}
				</fieldset>
				<div className="mt-4 grid grid-cols-2 gap-2 px-4 pb-4">
					<StudioButton
						variant="secondary"
						disabled={busy || selected.size === 0}
						onClick={() => void mark("cold")}
					>
						{busy && <Loader2 className="h-4 w-4 animate-spin" />}
						{t("realtor.followUps.mark.cold")}
					</StudioButton>
					<StudioButton
						disabled={busy || selected.size === 0}
						onClick={() => void mark("lost")}
					>
						{busy && <Loader2 className="h-4 w-4 animate-spin" />}
						{t("realtor.followUps.mark.lost")}
					</StudioButton>
				</div>
				{error && (
					<p
						role="alert"
						className="px-4 pb-4 text-sm text-dashboard-pill-risk"
					>
						{error}
					</p>
				)}
			</SheetContent>
		</Sheet>
	);
}
